import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

/**
 * Tolerancia en px para absorber diferencias de sub-pixel entre motores de render.
 * El texto de referencia entra con 0,2 px de margen, asi que sin esta holgura un
 * redondeo distinto podria bloquear el contenido por defecto. 0,5 px no habilita
 * nada util en la practica: es ruido de medicion, no una excepcion.
 */
export const FIT_TOLERANCE_PX = 0.5;

/** Milisegundos de espera antes de volver a medir mientras el usuario escribe. */
export const MEASURE_DEBOUNCE_MS = 120;

const WHITESPACE_ONLY = /^[\s\u00a0]*$/;
const EM_FONT_SIZE = /^([\d.]+)em$/;

/** Etiquetas de bloque a las que se les puede alinear el tamano de fuente. */
const BLOCK_SELECTOR = 'p, li, blockquote, h1, h2, h3, h4, h5, h6';

/**
 * Elementos de bloque que puede producir el editor.
 *
 * Sirven para distinguir la indentacion de autor (blanco ENTRE bloques, que no se
 * renderiza) de un separador inline (blanco junto a contenido inline, que SI se
 * renderiza).
 */
const BLOCK_TAGS = new Set([
    'p', 'li', 'blockquote', 'pre', 'div', 'ul', 'ol',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
]);

/** Un salto de linea dentro de un nodo de solo blancos delata indentacion. */
const LINE_BREAK = /[\n\r]/;

/**
 * @param {Node} node
 * @returns {boolean}
 */
function isBlockElement(node) {
    return node.nodeType === 1 && BLOCK_TAGS.has(node.tagName.toLowerCase());
}

/**
 * Elemento vecino de un nodo, saltando otros nodos de solo blancos.
 *
 * @param {Node} node
 * @param {'previousSibling'|'nextSibling'} direction
 * @returns {Node|null}
 */
function elementNeighbour(node, direction) {
    let sibling = node[direction];

    while (sibling) {
        if (sibling.nodeType === 1) {
            return sibling;
        }

        if (sibling.nodeType === 3 && WHITESPACE_ONLY.test(sibling.nodeValue || '')) {
            sibling = sibling[direction];
            continue;
        }

        return sibling;
    }

    return null;
}

/**
 * Indica si un nodo de solo blancos es indentacion entre bloques.
 *
 * La sonda mide con `white-space: pre-wrap`, asi que la indentacion del HTML
 * (`<p>a</p>\n<p>b</p>`) se convertiria en lineas visibles. Pero solo se puede
 * quitar ESA indentacion: un blanco junto a contenido inline -un espacio entre
 * `<strong>` y `<em>`, o el salto que separa dos fragmentos- SI se renderiza, y
 * borrarlo uniria las palabras y la sonda mediria un texto distinto del que se
 * guarda e imprime.
 *
 * Se exige salto de linea y bloques a ambos lados (o borde del contenedor). Un
 * blanco suelto entre bloques se conserva: medir de mas es el lado seguro.
 *
 * @param {Node} node
 * @returns {boolean}
 */
function isBlockIndentation(node) {
    if (!LINE_BREAK.test(node.nodeValue || '')) {
        return false;
    }

    const previous = elementNeighbour(node, 'previousSibling');
    const next = elementNeighbour(node, 'nextSibling');

    return (previous === null || isBlockElement(previous))
        && (next === null || isBlockElement(next));
}

/**
 * Ratio `em` de un `font-size` inline, o `null` si no es un valor en `em`.
 *
 * @param {string} value
 * @returns {number|null}
 */
function emRatio(value) {
    const match = EM_FONT_SIZE.exec((value || '').trim());

    return match ? parseFloat(match[1]) : null;
}

/**
 * Sube al párrafo el tamaño de fuente cuando TODO su texto comparte uno solo.
 *
 * Quill aplica el tamaño con un `<span>` dentro del `<p>`, pero el motor que
 * genera la plantilla NO usa el *strut* del bloque: dimensiona cada línea con el
 * tamaño del propio contenido. Medido sobre la plantilla, con letra chica el
 * avance de línea baja a 9,41 CSS px (0,725x), o sea que el `<p>` también se
 * achica.
 *
 * El navegador SÍ aplica el strut: un `<p>` a 10 px impone líneas de 13 px aunque
 * el span sea de 7,5 px. Medido en Chromium con el contenido real en letra chica:
 * la sonda daba 194,38 px donde la plantilla ocupa 154,1 px. Es decir, medía 40 px
 * de más y bloqueaba contenido que entra con margen (falso positivo).
 *
 * Al subir el tamaño al párrafo, el strut y el contenido coinciden y la medición
 * pasa a 155,38 px, a 1,3 px de la plantilla.
 *
 * Con tamaños mezclados en un mismo párrafo no se sube nada y la medición queda
 * conservadora (mide de más): el lado seguro.
 *
 * @param {Element} block
 * @param {number} baseFontSize
 */
function hoistUniformBlockFontSize(block, baseFontSize) {
    const text = block.textContent || '';

    if (!text.trim()) {
        return;
    }

    const covering = Array.from(block.querySelectorAll('*')).filter(
        element => emRatio(element.style.fontSize) !== null && element.textContent === text
    );

    if (covering.length !== 1) {
        return;
    }

    block.style.fontSize = `${baseFontSize * emRatio(covering[0].style.fontSize)}px`;

    // El span ya no debe volver a escalar respecto del nuevo tamaño del párrafo.
    covering.forEach(element => {
        element.style.fontSize = '';
    });
}

/**
 * Prepara el HTML para medirlo.
 *
 * Hace dos cosas, ambas justificadas por mediciones sobre la plantilla:
 *
 * 1. Quita la indentacion entre bloques (los saltos de linea y espacios que
 *    separan `<p>` de `<p>`). La sonda usa `white-space: pre-wrap` para respetar
 *    los saltos reales del contenido, pero eso también convierte la indentación
 *    del HTML en líneas visibles. Medido en Chromium: un texto de 211 px se mide
 *    como 313 px si no se normaliza. Solo se quita entre bloques: el blanco que
 *    separa contenido inline es contenido (ver `isBlockIndentation`).
 * 2. Sube al párrafo el tamaño de fuente cuando todo su texto comparte uno solo,
 *    para que el *strut* del bloque no infle la medición (ver
 *    `hoistUniformBlockFontSize`).
 *
 * @param {string} html
 * @param {number} [baseFontSize]
 * @returns {string} HTML listo para inyectar en la sonda
 */
export function normalizeMeasurableHtml(html, baseFontSize = 10) {
    if (!html) {
        return '';
    }

    // `window.DOMParser` y no `DOMParser` a secas: el entorno de test de jsdom no
    // expone el global, pero si `window.DOMParser`. Crea un documento inerte: no
    // ejecuta scripts ni dispara handlers `onerror`, a diferencia de asignar
    // innerHTML en un nodo del documento.
    const HTMLParser = typeof window !== 'undefined' ? window.DOMParser : null;

    if (!HTMLParser) {
        return html;
    }

    const parsed = new HTMLParser().parseFromString(html, 'text/html');
    const holder = parsed.body;

    const strip = node => {
        Array.from(node.childNodes).forEach(child => {
            if (child.nodeType === 3 && WHITESPACE_ONLY.test(child.nodeValue || '')) {
                if (isBlockIndentation(child)) {
                    node.removeChild(child);
                }

                return;
            }

            if (child.nodeType === 1) {
                strip(child);
            }
        });
    };

    strip(holder);

    holder.querySelectorAll(BLOCK_SELECTOR).forEach(block => {
        hoistUniformBlockFontSize(block, baseFontSize);
    });

    return holder.innerHTML;
}

/**
 * Decide si un contenido entra en la caja de impresion.
 *
 * `measuredHeight` debe ser el alto de una medicion REAL. Cuando no se pudo medir
 * -layout no disponible, sonda ausente, caja incompleta- hay que pasar `null` (o
 * cualquier valor no finito): devuelve `evaluable: false` y `fits: true`.
 *
 * «No se pudo medir» no es «se ha comprobado que cabe»: por eso `evaluable` es
 * false y el llamador no puede confundir una sonda ausente con una medida de 0.
 * Un alto 0 real (contenido vacio) si es una medicion valida y evaluable.
 *
 * `fits: true` con `evaluable: false` es la apertura deliberada: no se debe
 * bloquear al usuario porque el navegador no pueda medir.
 *
 * @param {{ measuredHeight?: number|null, maxHeight?: number, tolerancePx?: number }} params
 * @returns {{ evaluable: boolean, fits: boolean, overflowPx: number, measuredHeight: number, maxHeight: number }}
 */
export function evaluateFit({ measuredHeight, maxHeight, tolerancePx = FIT_TOLERANCE_PX } = {}) {
    const measurable =
        Number.isFinite(measuredHeight) &&
        Number.isFinite(maxHeight) &&
        maxHeight > 0;

    if (!measurable) {
        return {
            evaluable: false,
            fits: true,
            overflowPx: 0,
            measuredHeight: 0,
            maxHeight: 0
        };
    }

    const overflowPx = Math.max(0, measuredHeight - maxHeight - tolerancePx);

    return {
        evaluable: true,
        fits: overflowPx <= 0,
        overflowPx,
        measuredHeight,
        maxHeight
    };
}

/**
 * Forma de la caja de la plantilla que consume el motor.
 *
 * Es el contrato del motor: el hueco entre parrafos se llama
 * `paragraphSpacingPx` y va en **px fijos**, no en un `em` que escale con la
 * tipografia. Documentar la forma completa evita que un consumidor pase un
 * nombre que el motor ignora y acabe midiendo por debajo de la altura real.
 *
 * @typedef {Object} PrintBoxGeometry
 * @property {number} width               Ancho de la columna de texto, en px.
 * @property {number} maxHeight           Alto maximo de la caja, en px.
 * @property {number} baseFontSize        Tamano de fuente base, en px.
 * @property {number} lineHeight          Interlineado, sin unidad.
 * @property {number} paragraphSpacingPx  Hueco entre parrafos, en px fijos.
 * @property {number} [tolerancePx]       Holgura de sub-pixel; por defecto FIT_TOLERANCE_PX.
 */

/**
 * Estilos de la sonda que simula la caja de la plantilla de impresion.
 *
 * El margen entre parrafos se expone como variable CSS porque aplica a los <p>
 * del contenido, que llegan por `v-html` y no pueden recibir estilos inline.
 *
 * El hueco va en **px fijos, no en `em`**: medido sobre la plantilla, el hueco
 * extra entre parrafos es ~10,3 px tanto con letra normal como con letra chica.
 * En `em` escalaba con el tamano y quedaba por debajo del real.
 *
 * @param {PrintBoxGeometry} printBox
 * @returns {Object}
 */
export function buildProbeStyle(printBox) {
    return {
        width: `${printBox.width}px`,
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: `${printBox.baseFontSize}px`,
        lineHeight: String(printBox.lineHeight),
        '--print-box-paragraph-gap': `${printBox.paragraphSpacingPx}px`
    };
}

/**
 * Mide la altura renderizada de un contenido HTML dentro de una caja que simula
 * la plantilla de impresion, y decide si entra.
 *
 * La medicion real (`getBoundingClientRect`) solo funciona con layout de navegador.
 * En entornos sin layout devuelve `evaluable: false` y no bloquea.
 *
 * @param {{ html: import('vue').Ref<string>, printBox: import('vue').Ref<PrintBoxGeometry|null>, probeRef: import('vue').Ref<Element|null> }} params
 */
export function usePrintBoxFit({ html, printBox, probeRef }) {
    const measuredHtml = ref('');
    const measuredHeight = ref(0);
    /**
     * Si el alto de arriba viene de una medicion real o es el 0 por defecto.
     *
     * Sin esto, una sonda ausente daria alto 0 —finito— y `evaluateFit` lo leeria
     * como una medicion valida que «entra»: el editor mostraria «Entra en la
     * plantilla» y «0 / 211 px» sin haber medido nada. «No se pudo medir» no es
     * «se ha comprobado que cabe».
     */
    const hasMeasurement = ref(false);
    let pendingMeasure = null;

    const baseFontSize = computed(() =>
        printBox.value && Number.isFinite(printBox.value.baseFontSize)
            ? printBox.value.baseFontSize
            : 10
    );

    const isEnabled = computed(() =>
        Boolean(printBox.value && printBox.value.width > 0 && printBox.value.maxHeight > 0)
    );

    const printFit = computed(() =>
        evaluateFit({
            // `null` cuando no se midio: `evaluateFit` lo trata como no evaluable.
            measuredHeight: hasMeasurement.value ? measuredHeight.value : null,
            maxHeight: printBox.value ? printBox.value.maxHeight : 0,
            tolerancePx: printBox.value && Number.isFinite(printBox.value.tolerancePx)
                ? printBox.value.tolerancePx
                : FIT_TOLERANCE_PX
        })
    );

    const measure = () => {
        const node = probeRef.value;

        if (!node) {
            hasMeasurement.value = false;
            measuredHeight.value = 0;
            return;
        }

        hasMeasurement.value = true;
        measuredHeight.value = node.getBoundingClientRect().height;
    };

    const refresh = () => {
        if (!isEnabled.value) {
            measuredHtml.value = '';
            measuredHeight.value = 0;
            hasMeasurement.value = false;
            return;
        }

        measuredHtml.value = normalizeMeasurableHtml(html.value, baseFontSize.value);
        nextTick(measure);
    };

    const cancelPendingMeasure = () => {
        if (pendingMeasure) {
            clearTimeout(pendingMeasure);
            pendingMeasure = null;
        }
    };

    const scheduleRefresh = () => {
        cancelPendingMeasure();

        pendingMeasure = setTimeout(() => {
            pendingMeasure = null;
            refresh();
        }, MEASURE_DEBOUNCE_MS);
    };

    /**
     * Mide el contenido ACTUAL sin esperar el debounce y devuelve su ajuste.
     *
     * `printFit` se recalcula con retardo para no medir en cada tecla, asi que
     * entre un cambio de contenido y el vencimiento del debounce la medicion esta
     * obsoleta. Un guard de guardado no puede leer ese valor: tiene que forzar la
     * medicion pendiente y esperar el resultado del contenido que hay ahora.
     *
     * Cancela el temporizador en vuelo (si no, volveria a medir un poco despues y
     * pisaria el resultado) y mide tras el patch del DOM, que es lo que actualiza
     * la sonda.
     *
     * @returns {Promise<Object>} `printFit` del contenido actual
     */
    const flush = async() => {
        cancelPendingMeasure();
        refresh();

        await nextTick();
        // `refresh()` ya programa la medicion; repetirla es idempotente y asegura
        // que se lea el alto despues del patch del DOM.
        measure();

        return printFit.value;
    };

    watch(html, scheduleRefresh);
    watch(printBox, refresh, { immediate: true });

    onMounted(refresh);

    onBeforeUnmount(cancelPendingMeasure);

    return {
        measuredHtml,
        measuredHeight,
        printFit,
        isEnabled,
        measure,
        flush
    };
}
